// =============================================================================
// EMAIL TRIGGERS MANAGER - Configuration des règles de triggers automatiques
// =============================================================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { useGovernance } from "../../../hooks/useGovernance";
import {
  Zap,
  Plus,
  Trash2,
  Edit2,
  Mail,
  Tag,
  User,
  FileText,
  Bell,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Settings,
  Play,
  Pause,
  Filter,
  Clock
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITION_ICON_MAP = { sender: User, subject_contains: Mail, keyword: Tag, sender_domain: Mail };
const CONDITION_PLACEHOLDER_MAP = { sender: "@example.com", subject_contains: "facture, devis...", keyword: "urgent, paiement...", sender_domain: "anah.gouv.fr" };

const ACTION_ICON_MAP = { create_task: FileText, notify: Bell, advance_workflow: ArrowRight, link_dossier: FileText, auto_classify: Tag, auto_reply: Mail };
const ACTION_CONFIG_FIELDS = {
  create_task: ["task_title", "task_priority", "assign_to"],
  notify: ["notify_users", "notify_channel"],
  advance_workflow: ["target_status"],
  link_dossier: ["auto_link_timeline"],
  auto_classify: ["target_folder"],
  auto_reply: ["reply_template"],
};


export default function EmailTriggersManager({ tenantId }) {
  const { items: conditionTypeItems } = useGovernance("email_condition_types");
  const { items: actionTypeItems } = useGovernance("email_action_types");
  const { items: metierCategoryItems } = useGovernance("email_metier_categories");

  const conditionTypes = useMemo(() =>
    conditionTypeItems.map(i => ({ ...i, icon: CONDITION_ICON_MAP[i.id] || Tag, placeholder: CONDITION_PLACEHOLDER_MAP[i.id] || "" })),
    [conditionTypeItems]
  );
  const actionTypes = useMemo(() =>
    actionTypeItems.map(i => ({ ...i, icon: ACTION_ICON_MAP[i.id] || FileText, configFields: ACTION_CONFIG_FIELDS[i.id] || [] })),
    [actionTypeItems]
  );

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [scanning, setScanning] = useState(false);
  
  // Formulaire de règle
  const [ruleForm, setRuleForm] = useState({
    name: "",
    condition_type: "sender",
    condition_value: "",
    action_type: "create_task",
    action_config: {},
    priority: 0,
    enabled: true
  });

  // Headers avec tenant
  const getHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    "X-Tenant-ID": tenantId || "tef"
  }), [tenantId]);

  // Charger les règles
  const loadRules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email-triggers/rules`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || []);
      }
    } catch (error) {
      console.error("Erreur chargement règles:", error);
    }
  }, [getHeaders]);

  // Charger le statut
  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email-triggers/status`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (error) {
      console.error("Erreur chargement statut:", error);
    }
  }, [getHeaders]);

  // Chargement initial
  useEffect(() => {
    Promise.all([loadRules(), loadStatus()]).finally(() => setLoading(false));
  }, [loadRules, loadStatus]);

  // Créer une règle
  const createRule = async () => {
    try {
      const res = await fetch(`${API}/api/email-triggers/rules`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(ruleForm)
      });
      
      if (res.ok) {
        toast.success("Règle créée avec succès");
        setShowCreateDialog(false);
        resetForm();
        loadRules();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur création règle");
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  // Supprimer une règle
  const deleteRule = async (ruleId) => {
    if (!confirm("Supprimer cette règle ?")) return;
    
    try {
      const res = await fetch(`${API}/api/email-triggers/rules/${ruleId}`, {
        method: "DELETE",
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success("Règle supprimée");
        loadRules();
      }
    } catch (error) {
      toast.error("Erreur suppression");
    }
  };

  // Lancer un scan manuel
  const scanEmails = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/email-triggers/scan-new`, {
        method: "POST",
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success("Scan lancé en arrière-plan");
        setTimeout(loadStatus, 3000);
      }
    } catch (error) {
      toast.error("Erreur scan");
    } finally {
      setScanning(false);
    }
  };

  // Reset formulaire
  const resetForm = () => {
    setRuleForm({
      name: "",
      condition_type: "sender",
      condition_value: "",
      action_type: "create_task",
      action_config: {},
      priority: 0,
      enabled: true
    });
    setEditingRule(null);
  };

  // Ouvrir dialog création
  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  // Obtenir l'icône et couleur d'une action
  const getActionInfo = (actionType) => {
    return actionTypes.find(a => a.id === actionType) || actionTypes[0] || { id: actionType, label: actionType, icon: FileText, color: "gray" };
  };

  // Obtenir l'icône d'une condition
  const getConditionInfo = (conditionType) => {
    return conditionTypes.find(c => c.id === conditionType) || conditionTypes[0] || { id: conditionType, label: conditionType, icon: Tag };  
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-triggers-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-400" />
            Triggers Email Automatiques
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Configurez des actions automatiques basées sur les emails entrants
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={scanEmails}
            disabled={scanning}
            className="border-purple-500/30 text-purple-400"
          >
            {scanning ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Scanner maintenant
          </Button>
          <Button
            onClick={openCreateDialog}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle règle
          </Button>
        </div>
      </div>

      {/* Statut */}
      {status && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${status.status === 'active' ? 'bg-green-400' : 'bg-slate-500'}`} />
                  <span className="text-slate-300 text-sm">
                    {status.status === 'active' ? 'Service actif' : 'Service inactif'}
                  </span>
                </div>
                <div className="text-slate-400 text-sm">
                  <FileText className="w-4 h-4 inline mr-1" />
                  {status.tasks_created || 0} tâches créées
                </div>
                {status.last_processed && (
                  <div className="text-slate-400 text-sm">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Dernier scan: {new Date(status.last_processed).toLocaleString('fr-FR')}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des règles */}
      <div className="space-y-3">
        {rules.length === 0 ? (
          <Card className="bg-slate-800/30 border-slate-700 border-dashed">
            <CardContent className="p-8 text-center">
              <Zap className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-slate-300 font-medium mb-2">Aucune règle configurée</h3>
              <p className="text-slate-500 text-sm mb-4">
                Créez votre première règle pour automatiser le traitement des emails
              </p>
              <Button onClick={openCreateDialog} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Créer une règle
              </Button>
            </CardContent>
          </Card>
        ) : (
          rules.map((rule) => {
            const actionInfo = getActionInfo(rule.action_type);
            const conditionInfo = getConditionInfo(rule.condition_type);
            const ActionIcon = actionInfo.icon;
            const ConditionIcon = conditionInfo.icon;
            
            return (
              <Card 
                key={rule.id} 
                className={`bg-slate-800/50 border-slate-700 ${!rule.enabled ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Indicateur actif */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        rule.enabled ? `bg-${actionInfo.color}-500/20` : 'bg-slate-700'
                      }`}>
                        <ActionIcon className={`w-5 h-5 ${
                          rule.enabled ? `text-${actionInfo.color}-400` : 'text-slate-500'
                        }`} />
                      </div>
                      
                      {/* Détails */}
                      <div>
                        <h4 className="text-white font-medium flex items-center gap-2">
                          {rule.name}
                          {rule.priority > 0 && (
                            <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                              Priorité {rule.priority}
                            </Badge>
                          )}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                          <ConditionIcon className="w-3 h-3" />
                          <span>{conditionInfo.label}:</span>
                          <span className="text-slate-300">"{rule.condition_value}"</span>
                          <ArrowRight className="w-3 h-3 mx-1" />
                          <span className={`text-${actionInfo.color}-400`}>{actionInfo.label}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={async (checked) => {
                          // TODO: Implémenter toggle enabled
                          toast.info("Fonctionnalité à venir");
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRule(rule.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Catégories métier */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-cyan-400" />
            Catégories métier pour classification IA
          </CardTitle>
          <CardDescription>
            Ces catégories sont utilisées pour la classification automatique des emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {metierCategoryItems.map((cat) => (
              <Badge 
                key={cat.id}
                className={`bg-${cat.color || cat.id}-500/20 text-${cat.color || cat.id}-400 border-${cat.color || cat.id}-500/30`}
              >
                {cat.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog création règle */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              {editingRule ? "Modifier la règle" : "Nouvelle règle de trigger"}
            </DialogTitle>
            <DialogDescription>
              Définissez une condition et une action automatique
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Nom */}
            <div className="space-y-2">
              <Label className="text-slate-300">Nom de la règle</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="Ex: Factures installateurs"
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Condition */}
            <div className="space-y-2">
              <Label className="text-slate-300">Condition (SI...)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select
                  value={ruleForm.condition_type}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, condition_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {conditionTypes.map((ct) => (
                      <SelectItem key={ct.id} value={ct.id}>
                        <span className="flex items-center gap-2">
                          <ct.icon className="w-4 h-4" />
                          {ct.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={ruleForm.condition_value}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition_value: e.target.value })}
                  placeholder={getConditionInfo(ruleForm.condition_type)?.placeholder || ""}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Action */}
            <div className="space-y-2">
              <Label className="text-slate-300">Action (ALORS...)</Label>
              <Select
                value={ruleForm.action_type}
                onValueChange={(v) => setRuleForm({ ...ruleForm, action_type: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {actionTypes.map((at) => (
                    <SelectItem key={at.id} value={at.id}>
                      <span className="flex items-center gap-2">
                        <at.icon className={`w-4 h-4 text-${at.color}-400`} />
                        {at.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Config action spécifique */}
            {ruleForm.action_type === "create_task" && (
              <div className="space-y-2 p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-400 text-sm">Configuration de la tâche</Label>
                <Input
                  value={ruleForm.action_config.task_title || ""}
                  onChange={(e) => setRuleForm({
                    ...ruleForm,
                    action_config: { ...ruleForm.action_config, task_title: e.target.value }
                  })}
                  placeholder="Titre de la tâche (ou vide = sujet email)"
                  className="bg-slate-800 border-slate-700"
                />
                <Select
                  value={ruleForm.action_config.task_priority || "medium"}
                  onValueChange={(v) => setRuleForm({
                    ...ruleForm,
                    action_config: { ...ruleForm.action_config, task_priority: v }
                  })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Priorité" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {ruleForm.action_type === "advance_workflow" && (
              <div className="space-y-2 p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-400 text-sm">Statut cible</Label>
                <Select
                  value={ruleForm.action_config.target_status || ""}
                  onValueChange={(v) => setRuleForm({
                    ...ruleForm,
                    action_config: { ...ruleForm.action_config, target_status: v }
                  })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Choisir le statut" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="en_instruction_anah">En instruction ANAH</SelectItem>
                    <SelectItem value="valide_anah">Validé ANAH</SelectItem>
                    <SelectItem value="refuse_anah">Refusé ANAH</SelectItem>
                    <SelectItem value="paiement_en_cours">Paiement en cours</SelectItem>
                    <SelectItem value="paiement_ok">Paiement reçu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Priorité */}
            <div className="space-y-2">
              <Label className="text-slate-300">Priorité (ordre d'exécution)</Label>
              <Input
                type="number"
                value={ruleForm.priority}
                onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 0 })}
                min={0}
                max={100}
                className="bg-slate-800 border-slate-700 w-24"
              />
            </div>

            {/* Activée */}
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Règle activée</Label>
              <Switch
                checked={ruleForm.enabled}
                onCheckedChange={(checked) => setRuleForm({ ...ruleForm, enabled: checked })}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={createRule}
              disabled={!ruleForm.name || !ruleForm.condition_value}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {editingRule ? "Enregistrer" : "Créer la règle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
